import { createTheme } from "@mantine/core";
import { colors, THERAPPROVE_BLUE, THERAPPROVE_GREEN } from "./enums/Colors";
export const theme = createTheme({
    fontFamily: "Helvetica, sans-serif",
    // Set primary color
    primaryColor: "primary",
    primaryShade: 9,
    colors: {
        ...colors,
        primary: THERAPPROVE_GREEN,
        secondary: THERAPPROVE_BLUE
    },
    components: {
        Accordion: {
            styles: (_theme, props) => ({
                root: {
                    ...(props.variant === "outline" && {
                        borderRadius: "0.65rem",
                        boxShadow: "0 0 10px color-mix(in srgb, var(--mantine-color-black) 10%, transparent)",
                        backgroundColor: "white"
                    })
                },
                control: {
                    padding: "0.5rem"
                },
                label: {
                    padding: "0.25rem"
                }
            })
        },
        ActionIcon: {
            vars: (_theme, props) => ({
                root: {
                    "--ai-color": "color-mix(in srgb, var(--mantine-color-primary-9) 80%, transparent)",
                    "--ai-bg": "transparent",
                    "--ai-hover": "var(--mantine-color-primary-3)",
                    ...(props.variant === "filled" && {
                        "--ai-color": "white",
                        "--ai-bg": "color-mix(in srgb, var(--mantine-color-primary-9) 80%, transparent)",
                        "--ai-hover": "var(--mantine-color-primary-9)",
                        "--ai-radius": "1rem"
                    }),
                    ...(props.variant === "subtle" && {
                        "--ai-color": "black",
                        "--ai-bg": "transparent",
                        "--ai-hover": "color-mix(in srgb, var(--mantine-color-black) 10%, transparent)"
                    }),
                    ...(props.variant === "transparent" && {
                        "--ai-color": "color-mix(in srgb, var(--mantine-color-black) 50%, transparent)",
                        "--ai-bg": "transparent",
                        "--ai-hover": "transparent"
                    })
                }
            })
        },
        AppShell: {
            styles: {
                navbar: {
                    transition: "width 200ms ease"
                },
                main: {
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    minHeight: 0
                }
            }
        },
        Badge: {
            styles: (_theme, props) => ({
                root: {
                    textTransform: "none",
                    ...(props.variant === "dashboard-count-attention" && {
                        backgroundColor: "var(--mantine-color-yellow-1)",
                        color: "var(--mantine-color-yellow-8)",
                        border: "1px solid var(--mantine-color-yellow-2)"
                    }),
                    ...(props.variant === "dashboard-count-idle" && {
                        backgroundColor: "var(--mantine-color-gray-1)",
                        color: "var(--mantine-color-gray-7)",
                        border: "1px solid var(--mantine-color-gray-2)"
                    })
                }
            })
        },
        Button: {
            defaultProps: {
                size: "xs",
                color: "primary",
                variant: "filled"
            },
            vars: (_theme, props) => ({
                root: {
                    ...(props.variant === "light" && {
                        "--button-color": "black",
                        "--button-bg": "rgba(0, 180, 160, 0.3)",
                        "--button-hover": "rgba(0, 180, 160, 0.4)",
                        "--button-bd": "transparent"
                    })
                }
            }),
            styles: (_theme, props) => ({
                root: {
                    borderRadius: "0.65rem",
                    fontWeight: 600,
                    // This should be last so it overwrites other styling.
                    ...(props.disabled && {
                        cursor: "not-allowed"
                    })
                }
            })
        },
        Card: {
            defaultProps: {
                shadow: "xs",
                padding: "md",
                radius: "sm"
            },
            styles: (_theme, props) => ({
                root: {
                    ...(props.variant?.startsWith("dashboard-") && {
                        border: "1px solid var(--mantine-color-gray-2)",
                        background: "var(--mantine-color-white)",
                        boxShadow: "0 12px 32px color-mix(in srgb, var(--mantine-color-black) 6%, transparent)"
                    }),
                    ...(props.variant === "dashboard-summary-primary" && {
                        background: "linear-gradient(135deg, color-mix(in srgb, var(--mantine-color-primary-0) 90%, var(--mantine-color-white)), var(--mantine-color-white))",
                        border: "1px solid color-mix(in srgb, var(--mantine-color-primary-3) 55%, var(--mantine-color-white))"
                    }),
                    ...(props.variant === "dashboard-summary-secondary" && {
                        background: "linear-gradient(135deg, color-mix(in srgb, var(--mantine-color-secondary-0) 88%, var(--mantine-color-white)), color-mix(in srgb, var(--mantine-color-secondary-0) 52%, var(--mantine-color-white)))",
                        border: "1px solid color-mix(in srgb, var(--mantine-color-secondary-3) 34%, var(--mantine-color-white))",
                        boxShadow: "0 12px 32px color-mix(in srgb, var(--mantine-color-secondary-3) 10%, transparent)"
                    }),
                    ...(props.variant === "dashboard-summary-violet" && {
                        background: "linear-gradient(135deg, color-mix(in srgb, var(--mantine-color-violet-0) 94%, var(--mantine-color-white)), var(--mantine-color-white))",
                        border: "1px solid color-mix(in srgb, var(--mantine-color-violet-2) 70%, var(--mantine-color-white))"
                    }),
                    ...(props.variant === "dashboard-action-attention" && {
                        background: "linear-gradient(135deg, color-mix(in srgb, var(--mantine-color-yellow-0) 90%, var(--mantine-color-white)), var(--mantine-color-white))",
                        border: "1px solid color-mix(in srgb, var(--mantine-color-yellow-3) 58%, var(--mantine-color-white))"
                    }),
                    ...(props.variant === "dashboard-action-idle" && {
                        background: "linear-gradient(135deg, color-mix(in srgb, var(--mantine-color-primary-0) 72%, var(--mantine-color-white)), var(--mantine-color-white))",
                        border: "1px solid color-mix(in srgb, var(--mantine-color-primary-2) 48%, var(--mantine-color-white))"
                    })
                }
            })
        },
        Drawer: {
            defaultProps: {
                variant: "default",
                shadow: "md",
                size: "lg",
                position: "right",
                padding: "1rem",
            },
            styles: (_theme, props) => ({
                title: {
                    fontWeight: 550,
                    fontSize: 18
                },
                content: {
                    padding: 0,
                    display: "flex",
                    flexDirection: "column"
                },
                body: {
                    flex: 1,
                    paddingBottom: "1rem",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden"
                },
                ...(props.variant === "drawer-if" && {
                    content: {
                        display: "flex",
                        flexDirection: "column",
                        height: "100%"
                    },
                    body: {
                        height: "100%"
                    }
                })
            })
        },
        Chip: {
            styles: (_theme, props) => ({
                ...(props.variant === "compact" && {
                    iconWrapper: {
                        display: "none"
                    },
                    label: {
                        paddingTop: 4,
                        paddingBottom: 4,
                        paddingLeft: 10,
                        paddingRight: 10,
                        backgroundColor: props.checked
                            ? "var(--mantine-color-primary-9)"
                            : "var(--mantine-color-gray-1)",
                        color: props.checked
                            ? "white"
                            : "var(--mantine-color-gray-7)",
                        borderColor: props.checked
                            ? "var(--mantine-color-primary-9)"
                            : "var(--mantine-color-gray-3)"
                    }
                })
            })
        },
        Dropzone: {
            styles: {
                root: {
                    display: "flex",
                    flexDirection: "column",
                    minHeight: "7rem",
                    border: "2px dashed var(--mantine-color-gray-3)",
                    borderRadius: "12px",
                    backgroundColor: "white",
                    transition: "border-color 0.2s ease, background-color 0.2s ease",
                    padding: "5px"
                },
                inner: {
                    display: "flex",
                    flexDirection: "column",
                    flex: 1
                }
            }
        },
        Flex: {
            styles: (_theme, props) => ({
                root: {
                    ...(props.variant === "dialog-actions" && {
                        float: "right",
                        marginTop: "1rem",
                        marginBottom: "1rem"
                    }),
                    ...(props.variant === "image-preview" && {
                        height: 100,
                        width: 100,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "var(--mantine-color-gray-0)",
                        borderRadius: "12px",
                        overflow: "hidden",
                        border: "1px solid var(--mantine-color-gray-1)",
                        position: "relative"
                    })
                }
            })
        },
        Group: {
            styles: (_theme, props) => ({
                root: {
                    ...(props.variant === "centered" && {
                        flex: 1,
                        justifyContent: "center",
                        alignItems: "center"
                    })
                }
            })
        },
        Menu: {
            styles: {
                itemLabel: {
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    color: "var(--mantine-color-gray-8)"
                }
            }
        },
        Modal: {
            defaultProps: {
                variant: "default",
                centered: true,
                shadow: "md"
            },
            styles: (_theme, props) => ({
                title: {
                    fontWeight: 550,
                    fontSize: 18,
                    color: "var(--mantine-color-gray-8)"
                },
                ...(props.variant === "stepper" && {
                    body: {
                        height: "640px"
                    }
                }),
                ...(props.variant === "fullscreen" && {
                    content: {
                        height: "100vh",
                        maxHeight: "100vh",
                        maxWidth: "100vw"
                    },
                    body: {
                        height: "100vh",
                        maxHeight: "100vh",
                        padding: 0,
                        overflow: "hidden"
                    }
                }),
                ...(props.variant === "preview" && {
                    content: {
                        width: "auto",
                        maxWidth: "90vw"
                    },
                    body: {
                        height: "auto",
                        maxHeight: "90vh"
                    }
                }),
                ...(props.variant === "preview-wide" && {
                    content: {
                        width: "auto",
                        maxWidth: "80rem"
                    },
                    body: {
                        height: "auto",
                        maxHeight: "90vh"
                    }
                }),
                ...(props.variant === "preview-vertical" && {
                    content: {
                        width: "auto",
                        maxWidth: "25rem"
                    },
                    body: {
                        height: "auto",
                        maxHeight: "90vh"
                    }
                }),
                ...(props.variant === "preview-compact" && {
                    content: {
                        width: "auto",
                        maxWidth: "26vw"
                    },
                    body: {
                        height: "auto",
                        maxHeight: "26vh"
                    }
                })
            })
        },
        MultiSelect: {
            styles: (_theme, props) => ({
                input: {
                    // This needs to be here because, for some reason, when a variant is applied to only change the background color of the pill...
                    // the border goes away... so we re-apply it here.
                    border: "1px solid var(--mantine-color-gray-4)"
                },
                pill: {
                    ...(props.variant === "pill" && {
                        backgroundColor: "var(--mantine-color-primary-4)"
                    })
                }
            })
        },
        Pagination: {
            defaultProps: {
                size: "sm",
                radius: "xl",
                boundaries: 2
            }
        },
        Select: {
            defaultProps: {
                size: "xs"
            },
            styles: (_theme, props) => ({
                input: {
                    borderRadius: "0.5rem",
                    ...(props.variant === "unstyled" && {
                        textAlign: "center",
                        fontWeight: 500,
                        padding: 0,
                        minHeight: "auto",
                        fontSize: "12px",
                        color: "var(--mantine-color-gray-8)"
                    })
                }
            })
        },
        Skeleton: {
            styles: (_theme, props) => ({
                root: {
                    ...(props.variant === "card" && {
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        padding: "0.6rem",
                        borderRadius: "0.65rem",
                        transition: "all 0.2s ease-in-out",
                        marginBottom: "0.5rem"
                    })
                }
            })
        },
        Stack: {
            defaultProps: {
                gap: 0
            },
            styles: (_theme, props) => ({
                root: {
                    gap: props?.gap ? props.gap : 0,
                    ...(props.variant === "outline" && {
                        backgroundColor: "transparent",
                        boxShadow: "0 0 10px color-mix(in srgb, var(--mantine-color-black) 10%, transparent)",
                        borderRadius: "0.65rem",
                        padding: "0.5rem"
                    }),
                    ...(props.variant === "shadow" && {
                        borderRadius: "0.65rem",
                        boxShadow: "0 0 5px color-mix(in srgb, var(--mantine-color-black) 20%, transparent)"
                    })
                }
            })
        },
        Table: {
            defaultProps: {
                captionSide: "bottom"
            },
            styles: {
                table: {
                    tableLayout: "fixed"
                },
                th: {
                    fontSize: 12,
                    fontWeight: 600
                },
                tr: {
                    height: "min(8vh, 45px)",
                    borderBottom: "1px solid var(--mantine-color-gray-3)"
                },
                td: {
                    padding: 0,
                    paddingLeft: 10
                },
                caption: {
                    marginTop: 0,
                    borderTop: "1px solid var(--mantine-color-gray-3)",
                    paddingTop: "0.5rem",
                    position: "sticky",
                    bottom: 0,
                    zIndex: 2
                }
            }
        },
        Tabs: {
            styles: (_theme, _props) => ({
                tab: {
                    height: "40px",
                    fontSize: "small",
                    fontWeight: 800,
                    borderTopLeftRadius: "12px",
                    borderTopRightRadius: "12px",
                    transition: "all 0.3s ease"
                }
            })
        },
        TagsInput: {
            styles: (_theme, props) => ({
                input: {
                    ...(props.variant === "pill" && {
                        // This needs to be here because, for some reason, when a variant is applied to only change the background color of the pill...
                        // the border goes away... so we re-apply it here.
                        border: "1px solid var(--mantine-color-gray-4)"
                    })
                },
                pill: {
                    ...(props.variant === "pill" && {
                        backgroundColor: "var(--mantine-color-primary-4)"
                    })
                }
            })
        },
        Text: {
            styles: (_theme, props) => ({
                root: {
                    // Base text style (intentionally NOT truncated by default)
                    fontSize: 12,
                    fontWeight: 500,
                    color: "var(--mantine-color-gray-8)",
                    lineHeight: 1.4,
                    whiteSpace: "normal",
                    overflow: "visible",
                    textOverflow: "clip",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    hyphens: "auto",
                    // Labels / headings-in-text (single line)
                    ...(props.variant === "label" && {
                        fontSize: 14,
                        fontWeight: 700,
                        color: "var(--mantine-color-gray-8)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        overflowWrap: "break-word"
                    }),
                    ...(props.variant === "label-muted" && {
                        fontSize: 14,
                        fontWeight: 700,
                        color: "var(--mantine-color-gray-5)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        overflowWrap: "break-word",
                        textDecoration: "none"
                    }),
                    // Body variants
                    ...(props.variant === "body" && {
                        fontWeight: 500,
                        color: "var(--mantine-color-gray-8)"
                    }),
                    ...(props.variant === "body-muted" && {
                        fontWeight: 500,
                        color: "var(--mantine-color-gray-6)"
                    }),
                    ...(props.variant === "body-strong" && {
                        fontWeight: 600,
                        color: "var(--mantine-color-gray-7)"
                    }),
                    ...(props.variant === "paragraph" && {
                        lineHeight: 1.5,
                        whiteSpace: "normal",
                        lineClamp: undefined,
                        overflow: "visible",
                        textOverflow: "clip",
                        // Ensure long uninterrupted words wrap within containers
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                        hyphens: "auto"
                    }),
                    // Metadata / helper text (single line by default)
                    ...(props.variant === "meta" && {
                        fontSize: 11,
                        fontWeight: 500,
                        color: "var(--mantine-color-gray-8)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        overflowWrap: "break-word"
                    }),
                    ...(props.variant === "meta-muted" && {
                        fontSize: 11,
                        fontWeight: 500,
                        color: "var(--mantine-color-gray-6)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        overflowWrap: "break-word"
                    }),
                    ...(props.variant === "meta-strong" && {
                        fontSize: 11,
                        fontWeight: 600,
                        color: "var(--mantine-color-gray-5)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        overflowWrap: "break-word",
                        textDecoration: "none"
                    }),
                    ...(props.variant === "meta-hidden" && {
                        fontSize: 11,
                        fontWeight: 500,
                        color: "var(--mantine-color-gray-8)",
                        opacity: 0,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        overflowWrap: "break-word"
                    }),
                    ...(props.variant === "dashboard-summary-title" && {
                        fontSize: 14,
                        fontWeight: 600,
                        color: "var(--mantine-color-gray-8)"
                    }),
                    ...(props.variant === "dashboard-summary-value-primary" && {
                        fontSize: "1.75rem",
                        fontWeight: 700,
                        color: "var(--mantine-color-primary-9)",
                        lineHeight: 1.1
                    }),
                    ...(props.variant ===
                        "dashboard-summary-value-secondary" && {
                        fontSize: "1.75rem",
                        fontWeight: 700,
                        color: "var(--mantine-color-secondary-6)",
                        lineHeight: 1.1
                    }),
                    ...(props.variant === "dashboard-summary-value-violet" && {
                        fontSize: "1.75rem",
                        fontWeight: 700,
                        color: "var(--mantine-color-violet-8)",
                        lineHeight: 1.1
                    }),
                    ...(props.variant === "dashboard-summary-hint" && {
                        fontSize: 12,
                        fontWeight: 500,
                        color: "var(--mantine-color-gray-6)",
                        whiteSpace: "normal"
                    }),
                    ...(props.variant === "dashboard-action-title" && {
                        fontSize: 14,
                        fontWeight: 600,
                        color: "var(--mantine-color-gray-9)"
                    }),
                    ...(props.variant === "dashboard-action-hint" && {
                        fontSize: 14,
                        fontWeight: 500,
                        color: "var(--mantine-color-gray-6)",
                        whiteSpace: "normal"
                    }),
                    ...(props.variant ===
                        "dashboard-action-status-attention" && {
                        fontSize: 12,
                        fontWeight: 600,
                        color: "var(--mantine-color-yellow-8)"
                    }),
                    ...(props.variant === "dashboard-action-status-idle" && {
                        fontSize: 12,
                        fontWeight: 600,
                        color: "var(--mantine-color-primary-8)"
                    }),
                    ...(props.variant === "info" && {
                        color: "var(--mantine-color-blue-8)",
                        overflow: "visible",
                        textOverflow: "ellipsis",
                        whiteSpace: "normal",
                        lineClamp: undefined,
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                        hyphens: "auto",
                        maxWidth: "100%",
                        display: "block",
                        textAlign: "left"
                    }),
                    ...(props.variant === "error" && {
                        fontSize: 14,
                        fontWeight: 700,
                        color: "var(--mantine-color-red-5)",
                        textDecoration: "none"
                    })
                }
            })
        },
        Textarea: {
            styles: (_theme, props) => ({
                input: {
                    ...(props.variant === "codeBlock" && {
                        fontFamily: "monospace",
                        fontSize: "0.9rem",
                        backgroundColor: "var(--mantine-color-gray-1)",
                        padding: "12px",
                        border: "1px solid var(--mantine-color-gray-3)",
                        borderRadius: "4px"
                    })
                }
            })
        },
        TextInput: {
            defaultProps: {
                flex: 1,
                size: "xs"
            },
            styles: (_theme, props) => ({
                input: {
                    borderRadius: "0.5rem",
                    ...(props.variant === "unstyled" && {
                        textAlign: "center",
                        fontWeight: 700,
                        padding: 0,
                        minHeight: "auto",
                        fontSize: "12px",
                        color: "var(--mantine-color-gray-8)",
                        textOverflow: "ellipsis"
                    })
                }
            })
        },
        Title: {
            styles: (_theme, props) => ({
                root: {
                    color: "var(--mantine-color-gray-8)",
                    ...(props.variant === "page-title" && {
                        fontSize: 24,
                        color: "var(--mantine-color-primary-9)"
                    })
                }
            })
        }
    },
    other: {
    // 'Other' themes here
    }
});
export default theme;
